import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus, 
  FileUp, 
  Calendar, 
  Layers, 
  Trash2, 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminApi from '../../apis/adminApis';
import StatsCard from './components/StatsCard';
import RecentUsersList from './components/RecentUsersList';
import UsersByMonth from './components/UsersByMonth';

interface DashboardStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  superadmins: number;
  admins: number;
  user_media:number;
  regular_users: number;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  recent_users: any[];
  users_by_month: Array<{
    month: string;
    count: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await AdminApi.getStatistics();
      setStats(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard statistics. Please try again.');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStatistics();
  }, []);
  
  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Overview of user statistics and system activity</p>
        </div>
        <button 
          onClick={fetchStatistics}
          className="mt-4 md:mt-0 flex items-center gap-2 py-2 px-4 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <Trash2 className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {loading && !stats ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            <p className="mt-2 text-gray-500">Loading statistics...</p>
          </div>
        </div>
      ) : stats ? (
        <>
          {/* User Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard 
              title="Total Users"
              value={stats.total_users}
              icon={<Users className="h-6 w-6 text-blue-500" />}
              change={stats.new_users_this_month}
              changeLabel="this month"
              positive={true}
            />
            
            <StatsCard 
              title="Active Users"
              value={stats.active_users}
              icon={<UserPlus className="h-6 w-6 text-green-500" />}
              percentage={Math.round((stats.active_users / stats.total_users) * 100)}
              bgColor="bg-green-50"
            />
            
            <StatsCard 
              title="Media Files"
              value={stats?.user_media} // Sample value, replace with actual data
              icon={<FileUp className="h-6 w-6 text-orange-500" />}
              changeLabel="from last week"
              change={32}
              positive={true}
              bgColor="bg-orange-50"
            />
            
            <StatsCard 
              title="New Today"
              value={stats.new_users_today}
              icon={<Calendar className="h-6 w-6 text-purple-500" />}
              change={stats.new_users_today - 2} // Sample calculation
              changeLabel="from yesterday"
              positive={stats.new_users_today > 2}
              bgColor="bg-purple-50"
            />
          </div>
          
          {/* User Role Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">User Distribution</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.regular_users}</div>
                    <div className="text-sm text-gray-500">Regular Users</div>
                    <div className="mt-2 inline-flex items-center text-xs text-gray-500">
                      <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                      <span>{Math.round((stats.regular_users / stats.total_users) * 100)}% of total</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{stats.admins}</div>
                    <div className="text-sm text-gray-500">Admins</div>
                    <div className="mt-2 inline-flex items-center text-xs text-gray-500">
                      <TrendingDown className="h-3 w-3 text-gray-500 mr-1" />
                      <span>{Math.round((stats.admins / stats.total_users) * 100)}% of total</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.superadmins}</div>
                    <div className="text-sm text-gray-500">Superadmins</div>
                    <div className="mt-2 inline-flex items-center text-xs text-gray-500">
                      <TrendingDown className="h-3 w-3 text-gray-500 mr-1" />
                      <span>{Math.round((stats.superadmins / stats.total_users) * 100)}% of total</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 h-1 bg-gray-200 rounded-full">
                  <div 
                    className="h-1 bg-blue-500 rounded-full"
                    style={{ width: `${(stats.regular_users / stats.total_users) * 100}%` }}
                  />
                </div>
                <div className="mt-4 text-sm text-gray-500 text-center">
                  User Role Distribution
                </div>
              </div>
            </div>
            
            {/* User growth chart */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">User Growth</h3>
                <span className="text-sm text-gray-500">Last 6 months</span>
              </div>
              <div className="p-6">
                <UsersByMonth data={stats.users_by_month} />
              </div>
            </div>
          </div>
          
          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Recent Users</h3>
              <Link 
                to="/admin/users" 
                className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center"
              >
                <span>View all users</span>
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="p-6">
              <RecentUsersList users={stats.recent_users} />
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/admin/users/create" 
              className="bg-white rounded-lg shadow p-6 hover:bg-orange-50 transition-colors border-l-4 border-orange-500"
            >
              <div className="flex items-center">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <UserPlus className="h-6 w-6 text-orange-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
                  <p className="text-sm text-gray-500">Create a new user account</p>
                </div>
              </div>
            </Link>
            
            <Link 
              to="/admin/plans" 
              className="bg-white rounded-lg shadow p-6 hover:bg-blue-50 transition-colors border-l-4 border-blue-500"
            >
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Layers className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Manage Plans</h3>
                  <p className="text-sm text-gray-500">Configure subscription plans</p>
                </div>
              </div>
            </Link>
            
            <Link 
              to="/admin/reports" 
              className="bg-white rounded-lg shadow p-6 hover:bg-green-50 transition-colors border-l-4 border-green-500"
            >
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">View Reports</h3>
                  <p className="text-sm text-gray-500">Analytics and statistics</p>
                </div>
              </div>
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminDashboard;